import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import TokenWallet from "../TokenWallet";
import MySkins from "../MySkins";
import { getSkin, SkinPatternOverlay } from "../skins";
import {
  Settings, ChevronRight, LogOut, Shield, Trophy, Languages, X,
  Award, Users, UserCheck, BarChart3, Target, ChevronUp, ChevronDown,
} from "lucide-react";
import { COUNTRIES } from "../../lib/countries";

/**
 * V2 / Phase 4 — Profile tab.
 * Hero card with avatar + club + nationality flag + reliability gauge.
 * Stats grid, Followers/Following row (with WhatsApp unlock badge link),
 * Settings sheet (lang toggle), Admin link for staff, Logout.
 */
export default function ProfileV2() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.username) return;
      try {
        const { data: d } = await api.get(`/players/${user.username}`);
        if (!cancelled) setData(d);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.username]);

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const country = COUNTRIES.find((c) => c.code === user?.nationality);

  const onLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    nav("/login");
  };

  if (!user || user === false) {
    return (
      <div className="px-5 pt-12 text-center" data-testid="v2-profile-anon">
        <div className="w-16 h-16 rounded-2xl mx-auto bg-gradient-to-br from-[#7C3AED]/30 to-[#3B82F6]/30 border border-[#27272F] flex items-center justify-center mb-4">
          <Shield className="w-7 h-7 text-[#A5B4FC]" />
        </div>
        <h2 className="text-xl font-bold text-white">Sign in to view profile</h2>
        <Link to="/login" className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] rounded-2xl px-5 py-2.5 text-sm font-semibold text-white">
          Sign in <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const summary = data?.summary || {};
  const reliability = data?.reliability ?? user?.reliability ?? null;
  const activeSkin = getSkin(user?.active_skin);

  return (
    <div className="px-5 pt-5 pb-4 space-y-5" data-testid="v2-profile">
      {/* Header with settings cog */}
      <div className="flex items-center justify-between" data-testid="v2-profile-header">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A5B4FC]">{t("v2.prof.kicker")}</div>
          <h1 className="text-2xl font-bold text-white mt-0.5">{t("v2.prof.title")}</h1>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          data-testid="v2-profile-open-settings"
          className="p-2.5 rounded-xl bg-[#12121C] border border-[#1F1F2A] hover:border-[#7C3AED] transition-colors"
        >
          <Settings className="w-4 h-4 text-[#A1A1AA]" />
        </button>
      </div>

      {/* Hero card */}
      {(() => {
        const skin = activeSkin;
        const theme = skin ? {
          bg: skin.bg, border: skin.border, accent: skin.accent,
          text: skin.text, textMuted: skin.textMuted, chipBg: skin.accentSoft,
          shadow: skin.shadow, Icon: skin.icon, label: skin.label,
        } : {
          bg: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 35%, #3B82F6 100%)",
          border: "transparent", accent: "#FFFFFF", text: "#FFFFFF",
          textMuted: "rgba(255,255,255,0.8)", chipBg: "rgba(255,255,255,0.18)",
          shadow: "0 25px 50px -12px rgba(124,58,237,0.3)", Icon: null, label: null,
        };
        return (
          <section
            data-testid="v2-profile-hero"
            data-skin={user.active_skin || "default"}
            className="relative overflow-hidden rounded-3xl p-5"
            style={{ background: theme.bg, border: `1px solid ${theme.border}`, boxShadow: theme.shadow }}
          >
            {skin && <SkinPatternOverlay skinId={user.active_skin} />}
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none blur-2xl" style={{ backgroundColor: theme.chipBg }} />
            <div className="relative flex items-start gap-4">
              <div className="w-20 h-20 rounded-2xl backdrop-blur flex items-center justify-center font-bold text-3xl flex-shrink-0"
                   style={{ backgroundColor: theme.chipBg, color: theme.text }}>
                {(user.username || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>@{user.username}</span>
                  {country && <span className="text-[10px] font-medium" style={{ color: theme.textMuted }}>{country.flag || ""} {country.name}</span>}
                  {skin && theme.Icon && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-[0.18em]" style={{ backgroundColor: theme.chipBg, color: theme.text, border: `1px solid ${theme.border}` }} data-testid={`v2-profile-skin-tag-${user.active_skin}`}>
                      <theme.Icon className="w-2.5 h-2.5" />
                      {theme.label}
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-bold mt-1" style={{ color: theme.text }}>{user.club_name || t("v2.prof.noClub")}</h2>
                <div className="text-xs mt-1 capitalize" style={{ color: theme.textMuted }}>{user.platform || "—"}{user.role && user.role !== "player" ? ` · ${user.role}` : ""}</div>
              </div>
            </div>

            {/* Reliability gauge */}
            {reliability != null && (
              <div className="relative mt-5 backdrop-blur rounded-2xl p-3" style={{ backgroundColor: theme.chipBg }} data-testid="v2-profile-reliability">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>{t("v2.prof.reliability")}</span>
                  <span className="text-sm font-bold" style={{ color: theme.text }}>{Math.round(reliability)}/100</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0,0,0,0.25)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, reliability))}%`, backgroundColor: theme.accent }} />
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {/* Stats */}
      {loading ? (
        <div className="py-6 text-center text-sm text-[#52525B]">{t("v2.prof.loadingStats")}</div>
      ) : data ? (
        <section data-testid="v2-profile-stats">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">{t("v2.prof.performance")}</h3>
          <div className="grid grid-cols-3 gap-2">
            <StatCell icon={Trophy} label={t("v2.prof.played")} value={summary.played ?? 0} accent="#A5B4FC" />
            <StatCell icon={ChevronUp} label={t("v2.prof.won")} value={summary.won ?? 0} accent="#10B981" />
            <StatCell icon={ChevronDown} label={t("v2.prof.lost")} value={summary.lost ?? 0} accent="#EF4444" />
            <StatCell icon={Target} label={t("v2.prof.gf")} value={summary.gf ?? 0} accent="#A1A1AA" />
            <StatCell icon={Target} label={t("v2.prof.ga")} value={summary.ga ?? 0} accent="#71717A" />
            <StatCell icon={BarChart3} label={t("v2.prof.winPct")} value={summary.played ? `${Math.round((summary.won * 100) / summary.played)}%` : "0%"} accent="#7C3AED" />
          </div>
        </section>
      ) : null}

      {/* Token wallet */}
      <TokenWallet />

      {/* My Skins */}
      <MySkins />

      {/* Followers / Following */}
      <section data-testid="v2-profile-social-row">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">Connections</h3>
        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/v2/players/${user.username}`}
            data-testid="v2-profile-followers"
            className="bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl p-4 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-[#7C3AED]/20 text-[#A5B4FC] flex items-center justify-center"><Users className="w-4 h-4" /></span>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A]">Followers</div>
                <div className="text-lg font-bold text-white leading-tight">{data?.followers_count ?? 0}</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#52525B]" />
          </Link>
          <Link
            to={`/v2/players/${user.username}`}
            data-testid="v2-profile-following"
            className="bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl p-4 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-[#10B981]/20 text-[#10B981] flex items-center justify-center"><UserCheck className="w-4 h-4" /></span>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A]">Following</div>
                <div className="text-lg font-bold text-white leading-tight">{data?.following_count ?? 0}</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#52525B]" />
          </Link>
        </div>
      </section>

      {/* Quick links */}
      <section data-testid="v2-profile-links" className="space-y-1.5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-1.5">Account</h3>
        <RowLink to="/v2/account" icon={Settings} label="Account settings" testId="v2-profile-account" />
        <RowLink to={`/players/${user.username}`} icon={BarChart3} label="Full stats & H2H" testId="v2-profile-fullstats" />
        <RowLink to="/v2/my-matches" icon={Trophy} label="My matches" testId="v2-profile-mymatches" />
        {isStaff && (
          <RowLink to="/admin" icon={Shield} label="Admin panel" testId="v2-profile-admin" accent="#7C3AED" />
        )}
        {isStaff && (
          <RowLink to="/admin?tab=wc26" icon={Shield} label="World Cup · Manage all modes" testId="v2-profile-admin-wci" accent="#FFD700" />
        )}
      </section>

      {/* Logout */}
      <button
        onClick={onLogout}
        data-testid="v2-profile-logout"
        className="w-full flex items-center justify-center gap-2 bg-[#12121C] hover:bg-[#1A1A24] border border-[#1F1F2A] rounded-2xl py-3 text-sm font-semibold text-[#EF4444] transition-colors"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>

      {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
    </div>
  );
}

function StatCell({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl p-3 text-center">
      <Icon className="w-4 h-4 mx-auto" style={{ color: accent }} />
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}

function RowLink({ to, icon: Icon, label, testId, accent = "#A5B4FC" }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl px-4 py-3 flex items-center justify-between transition-colors"
    >
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}20`, color: accent }}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-sm font-semibold text-white">{label}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-[#52525B]" />
    </Link>
  );
}

function SettingsSheet({ onClose }) {
  const { lang, toggle } = useT();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      data-testid="v2-settings-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[480px] bg-[#0F0F18] border-t border-[#27272F] rounded-t-3xl p-5 space-y-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Settings</h3>
          <button onClick={onClose} data-testid="v2-settings-close" className="text-[#71717A] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Language */}
        <div className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl p-1 flex" data-testid="v2-settings-lang">
          <button
            onClick={() => { if (lang !== "en") toggle(); }}
            data-testid="v2-settings-lang-en"
            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-2 ${lang === "en" ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white" : "text-[#71717A]"}`}
          >
            <Languages className="w-3.5 h-3.5" /> English
          </button>
          <button
            onClick={() => { if (lang !== "ar") toggle(); }}
            data-testid="v2-settings-lang-ar"
            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-2 ${lang === "ar" ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white" : "text-[#71717A]"}`}
          >
            <Languages className="w-3.5 h-3.5" /> العربية
          </button>
        </div>
        <div className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#A5B4FC]/20 text-[#A5B4FC] flex items-center justify-center"><Award className="w-4 h-4" /></span>
            <div>
              <div className="text-sm font-semibold text-white">Theme</div>
              <div className="text-[11px] text-[#71717A]">v2 is dark-only · light mode dropped</div>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#52525B] text-center pt-2">ePlay Hub v2 · Phase 4</p>
      </div>
    </div>
  );
}
